import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import BasicTable from "../../shared/Table";
import { InsightsEmptyState, InsightsTableSkeleton } from "../../Insights/InsightsAsyncState";
import { StreetTeamInlineChart } from "./StreetTeamChart";
import StreetTeamRetailerDialog from "./StreetTeamRetailerDialog";

const linkButtonSx = {
  textTransform: "none",
  justifyContent: "flex-start",
  p: 0,
  minWidth: 0,
  fontWeight: 600,
  color: "#960B10",
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
};

const HEADERS = [
  { id: "label", key: "label", name: "Retailer", disabled: false },
  { id: "detail", key: "detail", name: "Visited by", disabled: false },
  { id: "count", key: "count", name: "Images", disabled: false },
];

function sortRetailers(items, sortBy, sortDirection) {
  const direction = sortDirection === "asc" ? 1 : -1;
  return [...items].sort((left, right) => {
    if (sortBy === "count") {
      const countDelta = (Number(left.count) || 0) - (Number(right.count) || 0);
      if (countDelta !== 0) {
        return countDelta * direction;
      }
    }
    const leftText = String(sortBy === "detail" ? left.detail : left.label || "");
    const rightText = String(sortBy === "detail" ? right.detail : right.label || "");
    return leftText.localeCompare(rightText, undefined, { sensitivity: "base" }) * direction;
  });
}

function ProvinceSection({ province, onOpenImages, onOpenCuts }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("count");
  const [sortDirection, setSortDirection] = useState("desc");
  const retailers = province.retailers || [];

  useEffect(() => {
    setPage(0);
  }, [province.id, retailers]);

  const sortedRows = useMemo(
    () => sortRetailers(retailers, sortBy, sortDirection),
    [retailers, sortBy, sortDirection]
  );
  const pageRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage).map((row) => ({
      id: row.id,
      label: (
        <Button
          size="small"
          variant="text"
          sx={linkButtonSx}
          onClick={() => onOpenCuts(row)}
          aria-label={`Open beef cut breakdown for ${row.label}`}
        >
          {row.label}
        </Button>
      ),
      detail: (row.visitors || []).join(", ") || "—",
      count: (
        <Button
          size="small"
          variant="text"
          sx={linkButtonSx}
          onClick={() => onOpenImages(row)}
          aria-label={`Open ${Number(row.count || 0).toLocaleString()} ${
            Number(row.count) === 1 ? "image" : "images"
          } for ${row.label}`}
        >
          {Number(row.count || 0).toLocaleString()}
        </Button>
      ),
    }));
  }, [onOpenCuts, onOpenImages, page, rowsPerPage, sortedRows]);

  const handleSort = (signedKey) => {
    const nextDirection = String(signedKey).startsWith("-") ? "desc" : "asc";
    setSortBy(String(signedKey).replace(/^-/, ""));
    setSortDirection(nextDirection);
    setPage(0);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>{province.label}</Typography>
      <Typography sx={{ fontWeight: 300, fontSize: "0.8rem", mb: 1 }}>
        {Number(province.count || 0).toLocaleString()} images · {retailers.length}{" "}
        {retailers.length === 1 ? "retailer" : "retailers"}
      </Typography>
      <Box sx={{ mb: 2 }}>
        <StreetTeamInlineChart
          title="Retailer comparison"
          subtitle={`- Image counts for retailers in ${province.label}.`}
          rows={retailers}
        />
      </Box>
      <BasicTable
        headers={HEADERS}
        items={pageRows}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
        totalCount={retailers.length}
        sortBy={sortBy}
        sortDirection={sortDirection}
        handleSort={handleSort}
        handleChangePage={(_, nextPage) => setPage(nextPage)}
        handleChangeRowsPerPage={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />
    </Box>
  );
}

export default function StreetTeamProvinceTables({
  provinces = [],
  isLoading = false,
  emptyMessage,
}) {
  const [selection, setSelection] = useState(null);
  const openImages = useCallback((retailer) => {
    setSelection({ mode: "images", retailer, open: true });
  }, []);
  const openCuts = useCallback((retailer) => {
    setSelection({ mode: "cuts", retailer, open: true });
  }, []);

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      {isLoading ? <InsightsTableSkeleton /> : null}
      {!isLoading && provinces.length === 0 ? <InsightsEmptyState message={emptyMessage} /> : null}
      {!isLoading && provinces.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {provinces.map((province) => (
            <ProvinceSection
              key={province.id}
              province={province}
              onOpenImages={openImages}
              onOpenCuts={openCuts}
            />
          ))}
        </Box>
      ) : null}
      <StreetTeamRetailerDialog
        open={Boolean(selection?.open)}
        mode={selection?.mode || "images"}
        retailer={selection?.retailer || null}
        onClose={() => setSelection((current) => (current ? { ...current, open: false } : null))}
        onExited={() => setSelection(null)}
      />
    </Box>
  );
}
